/**
 * StructuredFieldRenderer — Phase 2: Resolution Discipline
 * ─────────────────────────────────────────────────────────
 * Renders the field set returned by `getRequiredInputs(...)`. Maps each
 * `RequiredInputField.kind` to an existing UI primitive — no new visual
 * components are introduced here, only a thin mapping layer.
 *
 * Co-located with the situational layer so future surfaces can render
 * scenario-aware inputs without re-implementing this glue.
 */
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { RequiredInputField } from "@/data/situational/requiredInputs";

export type StructuredValues = Record<string, string | boolean>;

interface Props {
  fields: ReadonlyArray<RequiredInputField>;
  values: StructuredValues;
  onChange: (key: string, value: string | boolean) => void;
  /** Optional id prefix for stable, accessible field ids. */
  idPrefix?: string;
}

export function StructuredFieldRenderer({ fields, values, onChange, idPrefix = "sf" }: Props) {
  if (!fields.length) return null;

  return (
    <div className="space-y-3">
      {fields.map((field) => {
        const id = `${idPrefix}-${field.key}`;
        const value = values[field.key];

        return (
          <div key={field.key} className="space-y-1">
            <Label htmlFor={id} className="text-xs">
              {field.label} {field.required && <span className="text-destructive">*</span>}
            </Label>

            {field.kind === "select" && field.options && (
              <Select
                value={typeof value === "string" ? value : ""}
                onValueChange={(v) => onChange(field.key, v)}
              >
                <SelectTrigger id={id} className="h-9 text-xs">
                  <SelectValue placeholder="Choose…" />
                </SelectTrigger>
                <SelectContent>
                  {field.options.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} className="text-xs">
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {field.kind === "yesno" && (
              <div className="grid grid-cols-2 gap-1.5">
                <button
                  type="button"
                  onClick={() => onChange(field.key, true)}
                  className={`h-9 rounded-md text-xs font-semibold border transition-colors ${
                    value === true
                      ? "bg-primary/10 border-primary/40 text-primary"
                      : "bg-background border-border text-muted-foreground hover:border-muted-foreground/40"
                  }`}
                  aria-pressed={value === true}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => onChange(field.key, false)}
                  className={`h-9 rounded-md text-xs font-semibold border transition-colors ${
                    value === false
                      ? "bg-muted border-border text-foreground"
                      : "bg-background border-border text-muted-foreground hover:border-muted-foreground/40"
                  }`}
                  aria-pressed={value === false}
                >
                  No
                </button>
              </div>
            )}

            {field.kind === "text" && (
              <Input
                id={id}
                value={typeof value === "string" ? value : ""}
                placeholder={field.placeholder}
                onChange={(e) => onChange(field.key, e.target.value)}
                className="h-9 text-xs"
              />
            )}

            {field.kind === "textarea" && (
              <Textarea
                id={id}
                value={typeof value === "string" ? value : ""}
                placeholder={field.placeholder}
                onChange={(e) => onChange(field.key, e.target.value)}
                rows={3}
                className="text-xs resize-none"
              />
            )}

            {field.kind === "date" && (
              <Input
                id={id}
                type="date"
                value={typeof value === "string" ? value : ""}
                onChange={(e) => onChange(field.key, e.target.value)}
                className="h-9 text-xs"
              />
            )}

            {field.helper && (
              <p className="text-[10px] text-muted-foreground">{field.helper}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}
